"use client";

/**
 * Phase 71 — THE load-bearing i18n provider for this client-rendered SPA.
 *
 * This app has no Server Components fetching translated data (auth token in
 * localStorage, AuthGuard gates client-side, /me fetched via axios). So the live
 * language swap is driven by THIS React state passed explicitly to
 * NextIntlClientProvider — NOT by router.refresh() (Pitfall 1, a no-op here).
 *
 * Reconcile contract (W2): the useEffect watches user?.locale, which is set in two
 * places — (1) the first /me fetch after login (Plan 01 exposes data.locale), so the
 * persisted locale renders on initial load even before the switcher ships; and (2) at
 * switch-time, Plan 03's auth-store update mutates user.locale → this effect re-renders
 * in place. No exported setter is required. When i18n_enabled is false the locale is
 * hard-pinned to 'en' regardless of cookie/DB (Pitfall 5).
 */
import { NextIntlClientProvider } from "next-intl";
import { useEffect, useState } from "react";
import { readLocaleCookie, loadMessages } from "@/lib/i18n";
import { useAuthStore } from "@/stores/auth-store";

type Messages = Record<string, unknown>;

export function I18nBootstrap({
  children,
  initialLocale,
  initialMessages,
}: {
  children: React.ReactNode;
  initialLocale: string;
  initialMessages: Messages;
}) {
  const user = useAuthStore((s) => s.user);
  const i18nOn = user?.feature_flags?.i18n_enabled ?? false;
  // Per-clinic Lead→Patient terminology (FEATURE_PATIENTS_TERMINOLOGY). Known only after
  // /me resolves, so it rides the same one-frame catalog reconcile as the locale swap.
  const patientsTerm = user?.feature_flags?.patients_terminology_enabled ?? false;
  // Dentx 261030 — the chart number worded as a protocol number.
  const protocolTerm =
    (user?.feature_flags?.patient_chart_numbers_enabled ?? false) &&
    (user?.feature_flags?.chart_number_protocol_enabled ?? false);

  const [locale, setLocale] = useState(initialLocale);
  const [messages, setMessages] = useState<Messages>(initialMessages);

  // Reconcile the active catalog when the locale flag, DB locale, or terminology flag
  // changes. Flag off → force 'en' (Pitfall 5). Flag on → DB locale (source of truth) →
  // cookie → 'en'. Always reload (no target-equality short-circuit) so a terminology flip
  // is applied even when the locale itself is unchanged (e.g. en → en).
  useEffect(() => {
    const target = !i18nOn ? "en" : user?.locale ?? readLocaleCookie() ?? "en";
    loadMessages(target, patientsTerm, protocolTerm).then((m) => {
      setLocale(target);
      setMessages(m);
    });
    // Intentional reconcile: re-run only when these inputs change. The effect no longer
    // reads `locale`/`messages` state, so this dependency list is already exhaustive.
  }, [i18nOn, user?.locale, patientsTerm, protocolTerm]);

  return (
    <NextIntlClientProvider
      locale={locale}
      messages={messages}
      // Explicit tz = the browser's own. Same rendering as the implicit fallback,
      // but next-intl stops logging ENVIRONMENT_FALLBACK as a console error on
      // every dateTime format (first surfaced by the Smile admin dashboard).
      timeZone={Intl.DateTimeFormat().resolvedOptions().timeZone}
    >
      {children}
    </NextIntlClientProvider>
  );
}
